import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, FileText, Users, Trash2, Search, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PayslipDashboard = () => {
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('payslips');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    position: '',
    basic_salary: 0,
    phone: '',
    email: '',
    nric_last4: '',
    bank_name: '',
    bank_account: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [payslipsRes, employeesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/payslips/`),
        axios.get(`${BACKEND_URL}/api/payslips/employees`)
      ]);
      setPayslips(payslipsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/payslips/employees`, newEmployee);
      toast.success('Employee added successfully');
      setShowAddEmployee(false);
      setNewEmployee({
        name: '',
        position: '',
        basic_salary: 0,
        phone: '',
        email: '',
        nric_last4: '',
        bank_name: '',
        bank_account: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add employee');
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/payslips/employees/${employeeId}`);
      toast.success('Employee deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete employee');
    }
  };

  const handleDeletePayslip = async (payslipId) => {
    if (!window.confirm('Are you sure you want to delete this payslip?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/payslips/${payslipId}`);
      toast.success('Payslip deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payslip');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount || 0);
  };

  const filteredPayslips = payslips.filter(p => 
    p.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.pay_period?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEmployees = employees.filter(e => 
    e.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.position?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-32 px-4 flex justify-center items-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Payroll Management</h1>
            <p className="text-gray-600">Manage employees and payslips</p>
          </div>
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => setShowAddEmployee(true)}>
              <Users size={16} className="mr-2" />
              Add Employee
            </Button>
            <Link to="/admin/payslip/create">
              <Button>
                <Plus size={16} className="mr-2" />
                Create Payslip
              </Button>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <Button 
            variant={activeTab === 'payslips' ? 'default' : 'outline'}
            onClick={() => setActiveTab('payslips')}
          >
            <FileText size={16} className="mr-2" />
            Payslips ({payslips.length})
          </Button>
          <Button 
            variant={activeTab === 'employees' ? 'default' : 'outline'}
            onClick={() => setActiveTab('employees')}
          >
            <Users size={16} className="mr-2" />
            Employees ({employees.length})
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Payslips Tab */}
        {activeTab === 'payslips' && (
          <div className="space-y-4">
            {filteredPayslips.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  No payslips found. Create your first payslip.
                </CardContent>
              </Card>
            ) : (
              filteredPayslips.map(payslip => (
                <Card key={payslip.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-lg">{payslip.employee_name}</p>
                        <p className="text-gray-600">{payslip.position}</p>
                        <p className="text-sm text-gray-500">{payslip.pay_period}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">{formatCurrency(payslip.net_pay)}</p>
                        <p className="text-sm text-gray-500">Net Pay</p>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/admin/payslip/${payslip.id}`}>
                          <Button variant="outline" size="sm">
                            View
                          </Button>
                        </Link>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeletePayslip(payslip.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="space-y-4">
            {filteredEmployees.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  No employees found. Add your first employee.
                </CardContent>
              </Card>
            ) : (
              filteredEmployees.map(employee => (
                <Card key={employee.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-lg">{employee.name}</p>
                        <p className="text-gray-600">{employee.position}</p>
                        <p className="text-sm text-gray-500">{employee.phone} | {employee.email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-semibold">{formatCurrency(employee.basic_salary)}</p>
                        <p className="text-sm text-gray-500">Basic Salary</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteEmployee(employee.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Add Employee Modal */}
        {showAddEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
            <Card className="w-full max-w-md mx-4 bg-white">
              <CardHeader>
                <CardTitle>Add New Employee</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddEmployee} className="space-y-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={newEmployee.name}
                      onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Position *</Label>
                    <Input
                      value={newEmployee.position}
                      onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Basic Salary</Label>
                    <Input
                      type="number"
                      value={newEmployee.basic_salary}
                      onChange={(e) => setNewEmployee({...newEmployee, basic_salary: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={newEmployee.phone}
                        onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newEmployee.email}
                        onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input
                        value={newEmployee.bank_name}
                        onChange={(e) => setNewEmployee({...newEmployee, bank_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Bank Account</Label>
                      <Input
                        value={newEmployee.bank_account}
                        onChange={(e) => setNewEmployee({...newEmployee, bank_account: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 pt-4">
                    <Button type="submit" className="flex-1">Add Employee</Button>
                    <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddEmployee(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayslipDashboard;
